import { useState } from "react";
import { toast } from "sonner";
import { p, pu, errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, MessageCircle, Copy, Undo2, BadgeEuro } from "lucide-react";

const ETICHETTE = {
  da_chiedere: { t: "cauzione da chiedere", c: "bg-amber-100 text-amber-900" },
  richiesta: { t: "cauzione richiesta", c: "bg-sky-100 text-sky-900" },
  pagata: { t: "cauzione pagata", c: "bg-emerald-100 text-emerald-900" },
  rimborsata: { t: "cauzione restituita", c: "bg-slate-100 text-slate-600" },
};

/** Riga cauzione dentro la scheda della prenotazione. */
export const Caparra = ({ r, onCambio }) => {
  const stato = r.deposit_status || "non_richiesta";
  if (!r.deposit_required && stato === "non_richiesta") return null;
  const et = ETICHETTE[stato];

  const chiedi = async () => {
    try {
      const d = await p(`/reservations/${r.id}/deposit`, { origin_url: window.location.origin });
      if (d.whatsapp) window.open(d.whatsapp, "_blank");
      else {
        await navigator.clipboard.writeText(d.link);
        toast.success("Link copiato: mandalo al cliente");
      }
      onCambio?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 p-2" data-testid={`caparra-${r.id}`}>
      {et && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${et.c}`}>{et.t}</span>}
      {r.deposit_amount > 0 && (
        <span className="font-num text-xs font-bold">{Number(r.deposit_amount).toFixed(2)}€</span>
      )}
      {r.deposit_reason && <span className="text-xs text-slate-500">({r.deposit_reason})</span>}
      <div className="ml-auto flex items-center gap-2">
        {(stato === "da_chiedere" || stato === "richiesta") && (
          <Button size="sm" className="h-8" onClick={chiedi} data-testid={`caparra-chiedi-${r.id}`}>
            <MessageCircle className="mr-1 h-3.5 w-3.5" />
            {stato === "richiesta" ? "Rimanda il link" : "Chiedi cauzione"}
          </Button>
        )}
        {stato === "richiesta" && r.deposit_link && (
          <button
            className="text-xs font-semibold text-slate-500"
            data-testid={`caparra-copia-${r.id}`}
            onClick={async () => {
              await navigator.clipboard.writeText(r.deposit_link);
              toast.success("Link copiato");
            }}
          >
            <Copy className="mr-1 inline h-3.5 w-3.5" /> Copia link
          </button>
        )}
        {stato === "pagata" && (
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            data-testid={`caparra-rimborsa-${r.id}`}
            onClick={async () => {
              if (!window.confirm("Restituisco la cauzione al cliente?")) return;
              try {
                await p(`/reservations/${r.id}/deposit/refund`, {});
                toast.success("Cauzione restituita");
                onCambio?.();
              } catch (e) {
                toast.error(errMsg(e));
              }
            }}
          >
            <Undo2 className="mr-1 h-3.5 w-3.5" /> Restituisci
          </Button>
        )}
        {r.deposit_required && stato !== "pagata" && stato !== "rimborsata" && (
          <button
            className="text-xs font-semibold text-slate-400"
            data-testid={`caparra-rinuncia-${r.id}`}
            onClick={async () => {
              await p(`/reservations/${r.id}/deposit/waive`, {});
              toast.success("Cauzione non richiesta per questa prenotazione");
              onCambio?.();
            }}
          >
            Lascia stare
          </button>
        )}
      </div>
    </div>
  );
};

/** Impostazioni della cauzione: soglie, importo e date evento. */
export const CaparraConfig = ({ settings, onSalva }) => {
  const [s, setS] = useState({
    dep_on: settings?.dep_on || false,
    dep_min_people: settings?.dep_min_people ?? 8,
    dep_per_person: settings?.dep_per_person ?? 10,
    dep_noshow: settings?.dep_noshow ?? 2,
    dep_events: (settings?.dep_events || []).join(", "),
    waitlist_on: settings?.waitlist_on ?? true,
    waitlist_window: settings?.waitlist_window ?? 45,
  });

  const salva = async () => {
    const dep_events = s.dep_events
      .split(",")
      .map((x) => x.trim())
      .filter((x) => /^\d{4}-\d{2}-\d{2}$/.test(x));
    try {
      await pu("/settings", {
        ...settings,
        ...s,
        dep_min_people: Number(s.dep_min_people) || 8,
        dep_per_person: Number(s.dep_per_person) || 10,
        dep_noshow: Number(s.dep_noshow) || 2,
        waitlist_window: Number(s.waitlist_window) || 45,
        dep_events,
      });
      toast.success("Impostazioni salvate");
      onSalva?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Card className="space-y-3 p-4" data-testid="caparra-config">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
        <ShieldCheck className="mr-1.5 inline h-3.5 w-3.5" /> Cauzione e lista d'attesa
      </p>
      <label className="flex items-center gap-2 text-sm font-semibold">
        <input
          type="checkbox"
          className="h-4 w-4 accent-slate-900"
          checked={s.dep_on}
          onChange={(e) => setS({ ...s, dep_on: e.target.checked })}
          data-testid="dep-on"
        />
        Chiedi una cauzione sulle prenotazioni a rischio
      </label>
      {s.dep_on && (
        <div className="grid gap-2 sm:grid-cols-4">
          <label className="text-xs font-semibold text-slate-500">
            Da quante persone
            <Input
              type="number"
              min="2"
              className="mt-1"
              value={s.dep_min_people}
              onChange={(e) => setS({ ...s, dep_min_people: e.target.value })}
              data-testid="dep-min-people"
            />
          </label>
          <label className="text-xs font-semibold text-slate-500">
            Euro a persona
            <Input
              type="number"
              min="1"
              className="mt-1"
              value={s.dep_per_person}
              onChange={(e) => setS({ ...s, dep_per_person: e.target.value })}
              data-testid="dep-per-person"
            />
          </label>
          <label className="text-xs font-semibold text-slate-500">
            Dopo quante mancate presenze
            <Input
              type="number"
              min="1"
              className="mt-1"
              value={s.dep_noshow}
              onChange={(e) => setS({ ...s, dep_noshow: e.target.value })}
              data-testid="dep-noshow"
            />
          </label>
          <label className="text-xs font-semibold text-slate-500">
            Date evento (2026-12-31, …)
            <Input
              className="mt-1"
              placeholder="2026-12-31, 2027-02-14"
              value={s.dep_events}
              onChange={(e) => setS({ ...s, dep_events: e.target.value })}
              data-testid="dep-events"
            />
          </label>
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input
            type="checkbox"
            className="h-4 w-4 accent-slate-900"
            checked={s.waitlist_on}
            onChange={(e) => setS({ ...s, waitlist_on: e.target.checked })}
            data-testid="waitlist-on"
          />
          Lista d'attesa attiva
        </label>
        <label className="text-xs font-semibold text-slate-500">
          Tolleranza oraria per il posto liberato (minuti)
          <Input
            type="number"
            min="10"
            className="mt-1"
            value={s.waitlist_window}
            onChange={(e) => setS({ ...s, waitlist_window: e.target.value })}
            data-testid="waitlist-window"
          />
        </label>
      </div>
      <Button size="sm" onClick={salva} data-testid="caparra-config-salva">
        <BadgeEuro className="mr-1 h-3.5 w-3.5" /> Salva impostazioni
      </Button>
      <p className="text-xs text-slate-400">
        La cauzione si paga con carta dal link che mandi al cliente. In questa fase i pagamenti sono in
        modalità di prova: usa la carta 4242 4242 4242 4242 per provare.
      </p>
    </Card>
  );
};
